import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import { toast } from "sonner";
import { useApplication, useApplicationPromotionCheck, useApplications, useRoleBriefForPosition } from "@/api/hooks/admin";
import {
  useArchiveApplication,
  useDeclineApplication,
  useForwardApplication,
  usePromoteApplicationToCandidate,
  useUpdateApplication,
} from "@/api/hooks/mutations";
import * as api from "@/api/endpoints";
import type { AdminApplicationSummary } from "@/api/endpoints";
import { ApiError } from "@/api/client";
import { Button, buttonClass } from "@/components/ui/Button";
import { PaginationControls } from "@/components/ui/PaginationControls";
import { PillTabs } from "@/components/ui/PillTabs";
import { StatusTag } from "@/components/ui/StatusTag";
import { fmtDate, humanize, initials } from "@/lib/format";
import { safeUrl } from "@/lib/safe-url";

const STATUS_OPTIONS = [
  { value: "all" as const, label: "All" },
  { value: "new" as const, label: "New" },
  { value: "in_review" as const, label: "In review" },
  { value: "forwarded" as const, label: "Forwarded" },
  { value: "declined" as const, label: "Declined" },
  { value: "archived" as const, label: "Archived" },
];

type StatusValue = (typeof STATUS_OPTIONS)[number]["value"];
type ReviewStatus = "new" | "in_review";

export function AdminApplicationsPage() {
  const { id } = useParams();
  const detailId = id ? parseInt(id, 10) : null;
  const [searchParams, setSearchParams] = useSearchParams();

  const status = (searchParams.get("status") as StatusValue) ?? "all";
  const q = searchParams.get("q") ?? "";
  const page = parseInt(searchParams.get("page") ?? "1", 10);

  const list = useApplications({ status, q: q || undefined, page, page_size: 20 });

  const [searchDraft, setSearchDraft] = useState(q);
  useEffect(() => setSearchDraft(q), [q]);
  const debounceRef = useRef<number | null>(null);
  function onSearch(v: string) {
    setSearchDraft(v);
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(() => {
      const next = new URLSearchParams(searchParams);
      next.delete("page");
      if (v) next.set("q", v);
      else next.delete("q");
      setSearchParams(next, { replace: true });
    }, 300);
  }

  function setStatus(s: StatusValue) {
    const next = new URLSearchParams(searchParams);
    next.delete("page");
    if (s === "all") next.delete("status");
    else next.set("status", s);
    setSearchParams(next, { replace: true });
  }

  function setPage(nextPage: number) {
    const next = new URLSearchParams(searchParams);
    if (nextPage <= 1) next.delete("page");
    else next.set("page", String(nextPage));
    setSearchParams(next, { replace: true });
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Specialist applications</h1>
          <p className="mt-1 text-sm text-text-muted">
            {list.isLoading ? "Loading…" : `${list.data?.total ?? 0} total`}
          </p>
        </div>
        <input
          type="text"
          value={searchDraft}
          onChange={(e) => onSearch(e.target.value)}
          placeholder="Search name or email…"
          className="w-full rounded-md border border-border bg-surface px-3.5 py-2 text-sm outline-none focus:border-primary sm:w-72"
        />
      </div>

      <PillTabs value={status} onChange={setStatus} options={STATUS_OPTIONS} />

      <div className="flex flex-col gap-3 md:hidden">
        {list.isLoading ? (
          <MobileEmptyState>Loading…</MobileEmptyState>
        ) : !list.data?.items.length ? (
          <MobileEmptyState>No applications match.</MobileEmptyState>
        ) : (
          list.data.items.map((a) => <ApplicationCard key={a.id} row={a} />)
        )}
      </div>

      <div className="hidden overflow-x-auto rounded-2xl border border-border bg-surface md:block">
        <table className="min-w-[760px] w-full border-separate border-spacing-0 text-[13px]">
          <thead>
            <tr className="bg-surface-alt text-left text-[12px] font-semibold uppercase tracking-wider text-text-muted">
              <th className="px-4 py-3.5">Applicant</th>
              <th className="px-4 py-3.5">Position</th>
              <th className="px-4 py-3.5">Experience</th>
              <th className="px-4 py-3.5">Asking rate</th>
              <th className="px-4 py-3.5">Status</th>
              <th className="px-4 py-3.5">Submitted</th>
              <th className="px-4 py-3.5"></th>
            </tr>
          </thead>
          <tbody>
            {list.isLoading ? (
              <tr>
                <td colSpan={7} className="p-8 text-center text-text-muted">
                  Loading…
                </td>
              </tr>
            ) : !list.data?.items.length ? (
              <tr>
                <td colSpan={7} className="p-8 text-center text-text-muted">
                  No applications match.
                </td>
              </tr>
            ) : (
              list.data.items.map((a) => <ApplicationRow key={a.id} row={a} />)
            )}
          </tbody>
        </table>
      </div>

      {list.data && list.data.total > list.data.page_size ? (
        <PaginationControls
          page={list.data.page}
          pageSize={list.data.page_size}
          total={list.data.total}
          itemLabel="applications"
          onPageChange={setPage}
        />
      ) : null}

      {detailId != null && <ApplicationDetail id={detailId} />}
    </div>
  );
}

function ApplicationRow({ row }: { row: AdminApplicationSummary }) {
  return (
    <tr className="border-t border-border hover:bg-surface-alt">
      <td className="px-4 py-3.5">
        <div className="flex items-center gap-3">
          <div className="grid size-9 place-items-center rounded-full bg-gradient-to-br from-success to-primary text-[13px] font-bold text-white">
            {initials(row.full_name, row.email)}
          </div>
          <div>
            <div className="font-medium">{row.full_name}</div>
            <div className="text-text-muted">{row.email}</div>
          </div>
        </div>
      </td>
      <td className="px-4 py-3.5 text-text-muted">
        {row.position_id ? `#${row.position_id}` : "Open application"}
      </td>
      <td className="px-4 py-3.5">{row.years_experience}</td>
      <td className="px-4 py-3.5 font-semibold">
        {row.expected_rate} {row.currency}
      </td>
      <td className="px-4 py-3.5">
        <StatusTag status={row.status} />
      </td>
      <td className="px-4 py-3.5 text-text-muted">{fmtDate(row.created_at)}</td>
      <td className="px-4 py-3.5">
        <Link
          to={`/admin/applications/${row.id}`}
          className={buttonClass({ variant: "ghost", size: "sm" })}
        >
          Open →
        </Link>
      </td>
    </tr>
  );
}

function ApplicationCard({ row }: { row: AdminApplicationSummary }) {
  return (
    <article className="rounded-2xl border border-border bg-surface p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="font-semibold">{row.full_name}</div>
          <div className="mt-1 break-all text-sm text-text-muted">{row.email}</div>
        </div>
        <StatusTag status={row.status} />
      </div>
      <div className="mt-3 grid grid-cols-1 gap-2 text-sm text-text-muted">
        <div>{row.position_id ? `Position #${row.position_id}` : "Open application"}</div>
        <div>{row.years_experience}</div>
        <div>{row.expected_rate} {row.currency}</div>
        <div>{fmtDate(row.created_at)}</div>
      </div>
      <Link
        to={`/admin/applications/${row.id}`}
        className={`${buttonClass({ variant: "ghost", size: "sm" })} mt-4 w-full justify-center`}
      >
        Open
      </Link>
    </article>
  );
}

function ApplicationDetail({ id }: { id: number }) {
  const navigate = useNavigate();
  const detail = useApplication(id);
  const update = useUpdateApplication();
  const forward = useForwardApplication();
  const decline = useDeclineApplication();
  const promote = usePromoteApplicationToCandidate();
  const archive = useArchiveApplication();
  const roleBrief = useRoleBriefForPosition(detail.data?.position_id ?? null);
  const promotionCheck = useApplicationPromotionCheck(id);

  const [notes, setNotes] = useState("");
  const [reviewStatus, setReviewStatus] = useState<ReviewStatus>("new");
  const [confirmAction, setConfirmAction] = useState<CleanupAction>(null);
  const [declineReason, setDeclineReason] = useState("");
  useEffect(() => {
    if (!detail.data) return;
    setNotes(detail.data.admin_notes ?? "");
    setReviewStatus(detail.data.status === "in_review" ? "in_review" : "new");
  }, [detail.data, id]);

  if (detail.isLoading) {
    return (
      <section className="mt-6 rounded-2xl border border-border bg-surface p-6 text-text-muted">
        Loading…
      </section>
    );
  }
  if (detail.error) {
    return (
      <section className="mt-6 rounded-2xl border border-border bg-surface p-6 text-danger">
        {detail.error instanceof ApiError && detail.error.status === 404
          ? "This application no longer exists."
          : detail.error instanceof Error
            ? detail.error.message
            : "Could not load this application."}
      </section>
    );
  }
  if (!detail.data) {
    return (
      <section className="mt-6 rounded-2xl border border-border bg-surface p-6 text-text-muted">
        This application is unavailable.
      </section>
    );
  }

  const a = detail.data;
  const linkedCandidateId = (a as typeof a & { linked_candidate_id?: number | null }).linked_candidate_id ?? null;
  const cvHref = a.cv_blob_id ? api.blobUrl(a.cv_blob_id) : null;
  const portfolioHref = a.portfolio_blob_id ? api.blobUrl(a.portfolio_blob_id) : null;
  const canMoveIntoProcessing = a.status === "new" || a.status === "in_review";
  const inferredRoleBriefId = roleBrief.data?.id ?? null;
  const hasLinkedCandidate = linkedCandidateId != null;
  const promoteLabel = hasLinkedCandidate ? "Continue candidate processing" : "Move to candidate processing";
  const exactMatches = promotionCheck.data?.exact_matches ?? [];
  const fuzzyMatches = promotionCheck.data?.fuzzy_matches ?? [];

  async function onSave() {
    try {
      await update.mutateAsync({
        id,
        body: {
          admin_notes: notes,
          ...(canMoveIntoProcessing ? { status: reviewStatus } : {}),
        },
      });
      toast.success("Application updated");
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Save failed");
    }
  }

  async function onForward() {
    try {
      await forward.mutateAsync(id);
      toast.success("Forwarded to merchant");
      navigate("/admin/applications");
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        toast.error("Already in a terminal state.");
      } else {
        toast.error(err instanceof ApiError ? err.message : "Forward failed");
      }
    }
  }

  async function onDecline() {
    try {
      await decline.mutateAsync({ id, reason: declineReason.trim() || null });
      toast.success("Declined");
      navigate("/admin/applications");
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        toast.error("Already declined.");
      } else {
        toast.error(err instanceof ApiError ? err.message : "Decline failed");
      }
    }
  }

  async function onMoveIntoProcessing() {
    if (!canMoveIntoProcessing) return;

    try {
      if (a.status !== "in_review") {
        await update.mutateAsync({
          id,
          body: {
            admin_notes: notes,
            status: "in_review",
          },
        });
      }

      const candidate = await promote.mutateAsync({
        id,
        body: inferredRoleBriefId ? { role_brief_id: inferredRoleBriefId } : {},
      });

      if (hasLinkedCandidate) {
        toast.success(
          inferredRoleBriefId
            ? "Candidate processing updated and attached to the linked shortlist"
            : "Opened existing candidate processing",
        );
      } else {
        toast.success("Moved to candidate processing");
      }
      navigate("/admin/candidates/" + candidate.id);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Could not move application");
    }
  }

  async function onLinkExistingCandidate(candidateId: number) {
    try {
      if (a.status !== "in_review") {
        await update.mutateAsync({
          id,
          body: {
            admin_notes: notes,
            status: "in_review",
          },
        });
      }

      const candidate = await promote.mutateAsync({
        id,
        body: {
          existing_candidate_id: candidateId,
          ...(inferredRoleBriefId ? { role_brief_id: inferredRoleBriefId } : {}),
        },
      });
      toast.success("Linked to existing candidate");
      navigate("/admin/candidates/" + candidate.id);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Could not link candidate");
    }
  }

  async function onArchive() {
    try {
      await archive.mutateAsync(id);
      toast.success("Application archived");
      navigate("/admin/applications");
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Archive failed");
    }
  }

  return (
    <section className="mt-6 grid items-start gap-5 rounded-2xl border border-border bg-surface p-6 lg:grid-cols-[1fr_320px]">
      <div className="flex flex-col gap-5">
        <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-start sm:justify-between">
          <div>
            <h2 className="text-lg font-bold">
              Application: {a.full_name}
              {a.position_title ? ` — ${a.position_title}` : ""}
            </h2>
            <p className="mt-1 text-sm text-text-muted">
              Submitted {fmtDate(a.created_at)}
              {a.position_company ? ` for ${a.position_company}` : ""} ·{" "}
              <StatusTag status={a.status} />
            </p>
          </div>
          <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
            {canMoveIntoProcessing && (
              <Button onClick={onMoveIntoProcessing} loading={update.isPending || promote.isPending}>
                {promoteLabel}
              </Button>
            )}
            <Button variant="secondary" onClick={onForward} loading={forward.isPending}>
              Forward to merchant
            </Button>
            <Button variant="danger" onClick={() => setConfirmAction("decline")} loading={decline.isPending}>
              Decline
            </Button>
            <Button variant="secondary" onClick={() => setConfirmAction("archive")}>
              Archive application
            </Button>
            <button
              type="button"
              onClick={() => navigate("/admin/applications")}
              className={`${buttonClass({ variant: "ghost", size: "sm" })} w-full justify-center sm:w-auto`}
            >
              Close
            </button>
          </div>
        </div>

        {confirmAction && (
          confirmAction === "archive" ? (
            <CleanupConfirm
              noun="application"
              itemName={a.full_name}
              loading={archive.isPending}
              onCancel={() => setConfirmAction(null)}
              onConfirm={onArchive}
            />
          ) : (
            <DeclineConfirm
              noun="application"
              itemName={a.full_name}
              value={declineReason}
              loading={decline.isPending}
              onChange={setDeclineReason}
              onCancel={() => {
                setConfirmAction(null);
                setDeclineReason("");
              }}
              onConfirm={onDecline}
            />
          )
        )}

        <div className="rounded-xl bg-surface-alt p-4.5 text-sm leading-relaxed whitespace-pre-wrap">
          {a.cover_letter}
        </div>

        <div className="rounded-xl border border-border bg-surface-alt p-4 text-sm">
          <h3 className="mb-3 font-semibold">Submission context</h3>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Pair label="Source channel" value={humanize(a.source_channel)} />
            {a.source_campaign ? <Pair label="Source campaign" value={a.source_campaign} /> : null}
            <Pair
              label="Submission context"
              value={
                a.submission_context ? (
                  <div className="whitespace-pre-wrap">{a.submission_context}</div>
                ) : (
                  "—"
                )
              }
            />
          </div>
        </div>

        <div className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
          <Pair label="Full name" value={a.full_name} />
          <Pair label="Email" value={a.email} />
          <Pair label="Phone" value={a.phone} />
          <Pair label="Location" value={a.location || "—"} />
          <Pair label="LinkedIn" value={<UrlValue value={a.linkedin_url} />} />
          <Pair label="Telegram" value={<UrlValue value={a.telegram_url} />} />
          <Pair label="GitHub" value={<UrlValue value={a.github_url} />} />
          <Pair label="Current role" value={a.current_role || "—"} />
          <Pair label="Engagement" value={humanize(a.engagement_pref, "engagement")} />
          <Pair label="Years of experience" value={a.years_experience || "—"} />
          <Pair label="Asking rate" value={`${a.expected_rate} ${a.currency} / mo`} />
          <Pair label="Earliest start" value={a.earliest_start || "—"} />
          <Pair label="Notice period" value={a.notice_period || "—"} />
          <Pair
            label="Top skills"
            value={
              <div className="flex flex-wrap gap-1.5">
                {(a.skills ?? []).map((s) => (
                  <span
                    key={s}
                    className="rounded-full bg-primary-soft px-2 py-0.5 text-[11px] font-medium text-primary-hover"
                  >
                    {s}
                  </span>
                ))}
              </div>
            }
          />
          {a.additional_info && (
            <Pair
              label="Additional info"
              value={<div className="whitespace-pre-wrap">{a.additional_info}</div>}
            />
          )}
        </div>
      </div>

      <aside className="flex flex-col gap-4">
        <div className="rounded-xl border border-border bg-surface p-5">
          <h3 className="mb-3 text-base font-semibold">Files</h3>
          {cvHref || portfolioHref ? (
            <div className="flex flex-col gap-2.5">
              {cvHref && (
                <>
                  <a
                    href={cvHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={buttonClass({ variant: "secondary", size: "sm" })}
                  >
                    Preview
                  </a>
                  <a
                    href={cvHref}
                    download
                    className={buttonClass({ variant: "secondary", size: "sm" })}
                  >
                    Download CV
                  </a>
                </>
              )}
              {portfolioHref && (
                <a
                  href={portfolioHref}
                  download
                  className={buttonClass({ variant: "ghost", size: "sm" })}
                >
                  Download portfolio
                </a>
              )}
            </div>
          ) : (
            <div className="text-sm text-text-muted">No CV or portfolio attached.</div>
          )}
        </div>

        <div className="rounded-xl border border-border bg-surface p-5">
          <h3 className="mb-3 text-base font-semibold">Candidate processing</h3>
          <div className="space-y-3 text-sm">
            {exactMatches.length > 0 ? (
              <div className="rounded-lg border border-danger/30 bg-danger-soft px-3 py-2.5">
                <p className="font-medium text-danger">Exact duplicate matches found</p>
                <p className="mt-1 text-danger">
                  Link one of the existing candidates below instead of creating a duplicate record.
                </p>
              </div>
            ) : null}
            {linkedCandidateId ? (
              <div className="rounded-lg border border-warning/30 bg-warning-soft px-3 py-2.5">
                <p className="font-medium text-[#92400e]">Existing candidate record detected</p>
                <p className="mt-1 text-[#92400e]">
                  Backend linked this application to candidate #{linkedCandidateId}. Continuing processing will reuse that record
                  {inferredRoleBriefId ? " and keep it attached to the shortlist." : "."}
                </p>
              </div>
            ) : null}
            {exactMatches.length > 0 ? (
              <div className="space-y-3">
                {exactMatches.map((match) => (
                  <div key={match.candidate_id} className="rounded-lg border border-border bg-surface-alt p-3">
                    <p className="font-medium">{match.name}</p>
                    <p className="mt-1 text-text-muted">
                      Match via {humanize(match.reason)} · {match.current_title || match.email || "No extra identity"}
                    </p>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <Link
                        to={"/admin/candidates/" + match.candidate_id}
                        className={buttonClass({ variant: "ghost", size: "sm" })}
                      >
                        Open candidate #{match.candidate_id}
                      </Link>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => void onLinkExistingCandidate(match.candidate_id)}
                        loading={promote.isPending || update.isPending}
                      >
                        Link candidate #{match.candidate_id}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : fuzzyMatches.length > 0 ? (
              <div className="space-y-3">
                <p className="text-text-muted">
                  Possible fuzzy matches found. Review them before creating a new candidate record.
                </p>
                {fuzzyMatches.map((match) => (
                  <div key={match.candidate_id} className="rounded-lg border border-border bg-surface-alt p-3">
                    <p className="font-medium">{match.name}</p>
                    <p className="mt-1 text-text-muted">
                      Warning: {humanize(match.reason)} · {match.current_title || match.email || "No extra identity"}
                    </p>
                    <div className="mt-3">
                      <Link
                        to={"/admin/candidates/" + match.candidate_id}
                        className={buttonClass({ variant: "ghost", size: "sm" })}
                      >
                        Review candidate #{match.candidate_id}
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            ) : a.position_id == null ? (
              <p className="text-text-muted">
                Open applications can be reviewed here, but they are not attached to a role shortlist.
              </p>
            ) : roleBrief.isLoading ? (
              <p className="text-text-muted">Checking linked role shortlist…</p>
            ) : roleBrief.data ? (
              <>
                <p className="text-text-muted">
                  Promoting this application will open candidate processing and attach the candidate to the active role shortlist.
                </p>
                <Link
                  to={"/admin/role-rail/" + roleBrief.data.id}
                  className={buttonClass({ variant: "secondary", size: "sm" })}
                >
                  Open role shortlist
                </Link>
              </>
            ) : linkedCandidateId ? (
              <>
                <p className="text-text-muted">This application already has a linked candidate processing record.</p>
                <Link
                  to={"/admin/candidates/" + linkedCandidateId}
                  className={buttonClass({ variant: "secondary", size: "sm" })}
                >
                  Open candidate #{linkedCandidateId}
                </Link>
              </>
            ) : (
              <p className="text-text-muted">
                This position is not linked to a role brief yet. Use candidate processing first, then decide whether to attach it to a role later.
              </p>
            )}
          </div>
        </div>

        <div className="rounded-xl border border-border bg-surface p-5">
          <h3 className="mb-3 text-base font-semibold">Processing notes</h3>
          <label className="mb-3 block text-sm">
            <span className="mb-1.5 block font-medium text-text">Review status</span>
            <select
              aria-label="Application review status"
              value={canMoveIntoProcessing ? reviewStatus : a.status}
              onChange={(e) => setReviewStatus(e.target.value as ReviewStatus)}
              disabled={!canMoveIntoProcessing}
              className="w-full rounded-md border border-border bg-surface px-3 py-2.5 text-[13px] outline-none focus:border-primary disabled:cursor-not-allowed disabled:opacity-60"
            >
              <option value="new">New</option>
              <option value="in_review">In review</option>
            </select>
          </label>
          <textarea
            aria-label="Application internal notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Visible only to admins…"
            className="min-h-[120px] w-full resize-y rounded-md border border-border bg-surface px-3 py-2.5 text-[13px] outline-none focus:border-primary"
          />
          <Button
            variant="secondary"
            size="sm"
            className="mt-2.5 w-full"
            onClick={onSave}
            loading={update.isPending}
          >
            Save updates
          </Button>
        </div>
      </aside>
    </section>
  );
}

function MobileEmptyState({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded-2xl border border-border bg-surface p-6 text-center text-sm text-text-muted">
      {children}
    </div>
  );
}

function Pair({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <div className="text-text-muted">{label}</div>
      <div className="mt-0.5 font-medium">{value}</div>
    </div>
  );
}

function UrlValue({ value }: { value?: string | null }) {
  if (!value) return <>—</>;
  const safe = safeUrl(value);
  if (safe) {
    return (
      <a
        href={safe}
        target="_blank"
        rel="noopener noreferrer"
        className="text-primary hover:underline"
      >
        {value}
      </a>
    );
  }
  return <>{value}</>;
}

type CleanupAction = "archive" | "decline" | null;

function CleanupConfirm({
  noun,
  itemName,
  loading,
  onCancel,
  onConfirm,
}: {
  noun: string;
  itemName: string;
  loading: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <div
      role="alertdialog"
      aria-label={`Archive ${noun}`}
      className="rounded-xl border border-[#fecaca] bg-danger-soft p-4 text-sm"
    >
      <div className="font-semibold text-[#991b1b]">
        Archive this {noun}?
      </div>
      <p className="mt-1 text-[#7f1d1d]">
        This moves {itemName} out of active review while keeping the record available for audit.
      </p>
      <div className="mt-3 flex flex-wrap gap-2">
        <Button variant="ghost" size="sm" onClick={onCancel} disabled={loading}>
          Cancel
        </Button>
        <Button variant="danger" size="sm" onClick={onConfirm} loading={loading}>
          Confirm archive {noun}
        </Button>
      </div>
    </div>
  );
}

function DeclineConfirm({
  noun,
  itemName,
  value,
  loading,
  onChange,
  onCancel,
  onConfirm,
}: {
  noun: string;
  itemName: string;
  value: string;
  loading: boolean;
  onChange: (value: string) => void;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <div
      role="alertdialog"
      aria-label={`Decline ${noun}`}
      className="rounded-xl border border-[#fecaca] bg-danger-soft p-4 text-sm"
    >
      <div className="font-semibold text-[#991b1b]">
        Decline this {noun}?
      </div>
      <p className="mt-1 text-[#7f1d1d]">
        Add an optional reason for declining {itemName}. This stays attached to the record for audit.
      </p>
      <label className="mt-3 block">
        <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-[#991b1b]">
          Decline reason
        </span>
        <textarea
          aria-label="Decline reason"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="min-h-[96px] w-full resize-y rounded-md border border-[#fca5a5] bg-white px-3 py-2.5 text-[13px] text-text outline-none focus:border-[#ef4444]"
          placeholder="Optional internal context…"
        />
      </label>
      <div className="mt-3 flex flex-wrap gap-2">
        <Button variant="ghost" size="sm" onClick={onCancel} disabled={loading}>
          Cancel
        </Button>
        <Button variant="danger" size="sm" onClick={onConfirm} loading={loading}>
          Confirm decline {noun}
        </Button>
      </div>
    </div>
  );
}
